import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

interface SortConfig {
  columnIndex: number | null;
  direction: 'asc' | 'desc';
}

interface TableCell {
  text: string;
  sortValue?: string;
}

interface TableRow {
  cells: TableCell[];
  originalElement: HTMLTableRowElement;
}

interface SortableTableProps {
  table: HTMLTableElement;
}

const SortableTable: React.FC<SortableTableProps> = ({ table }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const sortColumn = table.getAttribute('data-sort-column');
    const sortDirectionAttr = table.getAttribute('data-sort-direction');

    if (sortColumn && sortDirectionAttr) {
      const direction = sortDirectionAttr.toLowerCase().startsWith('asc') ? 'asc' : 'desc';

      const headerRow = table.querySelector('thead tr');
      if (headerRow) {
        const headerCells = Array.from(headerRow.querySelectorAll<HTMLTableCellElement>('th'));
        const columnIndex = headerCells.findIndex(cell => cell.getAttribute('data-field-name') === sortColumn);

        if (columnIndex >= 0) {
          return { columnIndex, direction };
        }
      }
    }

    return { columnIndex: null, direction: 'asc' };
  });
  const [rows, setRows] = useState<TableRow[]>([]);
  const [tableElement, setTableElement] = useState<HTMLTableElement | null>(null);

  useEffect(() => {
    const headerRow = table.querySelector('thead tr');
    const bodyRows = table.querySelectorAll('tbody tr');

    if (!headerRow || bodyRows.length === 0) return;

    const headerCells = headerRow.querySelectorAll<HTMLTableCellElement>('th');

    const rowData: TableRow[] = Array.from(bodyRows).map(row => ({
      cells: Array.from(row.querySelectorAll('td')).map(cell => ({
        text: cell.textContent?.trim() ?? '',
        sortValue: cell.getAttribute('data-value') ?? undefined,
      })),
      originalElement: row as HTMLTableRowElement,
    }));

    setRows(rowData);
    setTableElement(table);

    const handleSort = (index: number) => {
      setSortConfig(prev => ({
        columnIndex: index,
        direction: prev.columnIndex === index && prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    };

    headerCells.forEach((header, index) => {
      if (header.hasAttribute('data-no-sort')) return;

      header.tabIndex = 0; // Accessibility

      const onClick = () => handleSort(index);
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort(index);
        }
      };

      header.addEventListener('click', onClick);
      header.addEventListener('keydown', onKeyDown);

      // Cleanup listeners on unmount
      return () => {
        header.removeEventListener('click', onClick);
        header.removeEventListener('keydown', onKeyDown);
      };
    });
  }, [table]);

  useEffect(() => {
    if (!tableElement || sortConfig.columnIndex === null) return;

    const sortedRows = [...rows].sort((a, b) => {
      const aCell = a.cells[sortConfig.columnIndex];
      const bCell = b.cells[sortConfig.columnIndex];
      const aValue = aCell.sortValue ?? aCell.text;
      const bValue = bCell.sortValue ?? bCell.text;

      const aTrimmed = aValue.trim();
      const bTrimmed = bValue.trim();
      const aNum = Number(aTrimmed);
      const bNum = Number(bTrimmed);
      if (aTrimmed !== '' && bTrimmed !== '' && !isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    const tbody = tableElement.querySelector('tbody');
    if (tbody) {
      tbody.innerHTML = '';
      sortedRows.forEach(row => tbody.appendChild(row.originalElement));
    }

    const headerCells = tableElement.querySelectorAll('thead tr th');
    headerCells.forEach((header, index) => {
      const indicator = header.querySelector('i.fa-chevron-up, i.fa-chevron-down');
      if (indicator) indicator.remove();

      if (index === sortConfig.columnIndex) {
        const newIndicator = document.createElement('i');
        newIndicator.className = `fas fa-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`;
        header.appendChild(newIndicator);

        // Update hidden form fields for postback
        const fieldName = header.getAttribute('data-field-name');
        const form = tableElement.closest('form');
        if (form && fieldName) {
          const columnInput = form.querySelector<HTMLInputElement>('input[name="SortOptions.Column"]');
          const directionInput = form.querySelector<HTMLInputElement>('input[name="SortOptions.Direction"]');
          if (columnInput) columnInput.value = fieldName;
          if (directionInput) directionInput.value = sortConfig.direction === 'asc' ? 'Ascending' : 'Descending';
        }
      }
    });
  }, [sortConfig, rows, tableElement]);

  return null;
};

export default function enhanceSortableTables(): void {
  document.querySelectorAll<HTMLTableElement>('table[data-sortable]').forEach(table => {
    const mount = document.createElement('span');
    mount.style.display = 'none';
    table.after(mount);
    createRoot(mount).render(<SortableTable table={table}/>);
  });
}
